import { TShowSystemEmailDialog } from "./types"
import { t } from "i18next"
import {
    DialogActions,
    DialogTitle,
    Dropdown,
    TSystemEmail,
    Typography,
    DialogHandler,
    EApiLanguages,
    TemplatesFactory,
    SendersFactory,
    SystemEmailsFactory,
    SystemEmailsModel,
    fetchRoute
} from "@cakemail-org/ui-components-v2"
import { Box, DialogContent, Link, SelectChangeEvent } from "@mui/material"
import { MouseEvent, ReactNode, useEffect, useRef, useState } from "react"
import { getLanguagesOptions } from "../../../../data/language"
import { ESystemEmailType } from "../tableListing/types"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"
import "./styles.scss"
import { TDropMenuItem } from "@cakemail-org/ui-components-v2/dist/esm/components/dropMenu/types"
import { validateSystemEmailContent } from "../../../../utils/components"
import { ERoutes } from "../../../../utils/url"
import { TValidateSystemEmailContentReturn } from "../../../../utils/components/types"

export function showSystemEmailDialog(args: TShowSystemEmailDialog) {
    DialogHandler.show({
        managedByContent: true,
        content: <SystemEmailDialogContent {...args} />
    })
}

function SystemEmailDialogContent({
    partnerBrand,
    initTemplateId,
    initSenderId,
    initLanguage,
    initSysEmailName,
    action,
    initSystemEmails,
    onSuccess,
    onError,
    account
}: TShowSystemEmailDialog) {
    const systemEmailsRef = useRef<SystemEmailsModel | undefined>(initSystemEmails)
    const [systemEmail, setSystemEmail] = useState<TSystemEmail>({
        language: initLanguage || EApiLanguages.en_US,
        sender: {
            id: initSenderId || ""
        },
        template: {
            id: initTemplateId || 0
        }
    })
    const [systemEmailName, setSystemEmailName] = useState(initSysEmailName || ESystemEmailType.accountConfirm)

    const [templatesDD, setTemplatesDD] = useState<TDropMenuItem[]>([])
    const [sendersDD, setSendersDD] = useState<TDropMenuItem[]>([])

    const [errors, setErrors] = useState<TValidateSystemEmailContentReturn>()

    const isPartnerOrOrganization = account.isPartnerOrOrganization()

    useEffect(() => {
        fetchSenders()
        if (initTemplateId) {
            validateTemplateContent(initTemplateId)
        }
        if (!systemEmailsRef.current) {
            SystemEmailsFactory.list()
                .then((data) => {
                    systemEmailsRef.current = data
                })
                .catch((error) => {
                    onError && onError(error)
                    DialogHandler.hide()
                })
        }
    }, [])

    useEffect(fetchTemplates, [systemEmailName])

    function validateTemplateContent(tempalteId: number) {
        if (!tempalteId) {
            return
        }
        setErrors([])
        TemplatesFactory.get({ id: tempalteId }).then((data) => {
            setErrors(validateSystemEmailContent({ template: data, systemEmailType: systemEmailName }))
        }).catch(() => {
            setErrors([{
                value: "",
                msg: t("system-emails-req-tag-generic-error")
            }])
        })
    }

    function fetchSenders() {
        SendersFactory.list({ perPage: 200 })
            .then((sendersList) => {
                setSendersDD(sendersList.data.filter((s) => s.confirmed).map((s) => {
                    return {
                        value: String(s.id),
                        text: `${s.name} (${s.email})`,
                    }
                }))
            })
            .catch((error) => {
                onError && onError(error)
                DialogHandler.hide()
            })
    }

    function fetchTemplates() {
        TemplatesFactory.list({ perPage: 200, filter: `tag==usage_${systemEmailName};is_not_owner==true;is_owner==true` })
            .then((templateList) => {
                setTemplatesDD(templateList.data.map((t) => {
                    return {
                        value: String(t.id),
                        text: t.name,
                        preContent: <img width={60} height={90} style={{ borderRadius: "8px", border: "1px solid #ddd" }} src={t.thumbnail_url.replace("/200/110", "/60/90")} />
                    }
                }))
            })
            .catch((error) => {
                onError && onError(error)
                DialogHandler.hide()
            })
    }

    function handleConfirm(event: MouseEvent<HTMLButtonElement>, btnRef: TButtonRef) {
        btnRef && btnRef.animate()
        let currentSysEmail = systemEmailsRef.current?.[systemEmailName] || []

        if (action === "edit" && initLanguage) {
            currentSysEmail = currentSysEmail.filter((sysEmail) => sysEmail.language !== initLanguage)
        }

        SystemEmailsFactory.update({
            systemEmails: {
                [systemEmailName]: [
                    ...currentSysEmail,
                    {
                        language: systemEmail.language,
                        sender: {
                            id: systemEmail.sender?.id
                        },
                        template: {
                            id: systemEmail.template?.id
                        }
                    }]
            }
        })
            .then((data) => {
                btnRef && btnRef.reset()
                DialogHandler.hide()
                onSuccess && onSuccess(data)
            })
            .catch((error) => {
                btnRef && btnRef.reset()
                DialogHandler.hide()
                onError && onError(error)
            })


    }

    function handleCancel(event: MouseEvent<HTMLButtonElement>, btnRef: TButtonRef) {
        DialogHandler.hide()
    }

    function handleDDChange(event: SelectChangeEvent<string>, child: ReactNode) {
        const { name, value } = event.target
        switch (name) {
            case "systemEmail":
                setSystemEmailName(value as ESystemEmailType)
                validateTemplateContent(Number(systemEmail.template?.id))
                break
            case "language":
                setSystemEmail({
                    ...systemEmail,
                    language: value as EApiLanguages
                })
                break
            case "sender":
            case "template":
                if (name === "template") {
                    validateTemplateContent(Number(value))
                }
                setSystemEmail({
                    ...systemEmail,
                    [name]: { id: value }
                })
                break
        }
    }



    const languageList = getLanguagesOptions({ partnerBrand, useApiLanguages: true })
    let systemEmailTypes = isPartnerOrOrganization ?
        Object.values(ESystemEmailType)
            .map((type, index) => {
                return {
                    text: t("system-email-tab-" + type),
                    value: type
                }
            }) : [{
                text: t("system-email-tab-" + ESystemEmailType.contactDOptIn),
                value: ESystemEmailType.contactDOptIn
            }]

    const currentLanguage = systemEmail.language
    const currentSender = systemEmail.sender?.id || undefined
    const currentTemplate = systemEmail.template?.id ? String(systemEmail.template.id) : undefined

    const isEnabled = systemEmailName && currentLanguage && currentSender && currentTemplate && !errors

    return <Box className="systemEmailEditDialog">
        <DialogTitle variant="h2">
            {t("system-emails-dialog-title-" + action)}
        </DialogTitle>
        <DialogContent>
            <Dropdown
                menuPlacement="bottom-left"
                label={t("system-emails-dialog-type-label")}
                placeholder={t("system-emails-dialog-type-placeholder")}
                onChange={handleDDChange}
                options={systemEmailTypes}
                value={systemEmailName}
                name="systemEmail"
            //disabled={!isPartnerOrOrganization}
            />

            <Dropdown
                menuPlacement="bottom-left"
                label={t("general-language")}
                placeholder={t("general-language-select-placeholder")}
                onChange={handleDDChange}
                options={languageList}
                value={currentLanguage}
                name="language"
            />

            <Dropdown
                menuPlacement="bottom-left"
                label={t("general-sender_other")}
                placeholder={t("general-senders-select-placeholder")}
                onChange={handleDDChange}
                options={sendersDD}
                value={currentSender}
                name="sender"
            />

            <Dropdown
                menuPlacement="bottom-left"
                label={t("templates-dd-label")}
                placeholder={t("templates-dd-placeholder")}
                onChange={handleDDChange}
                options={templatesDD}
                value={currentTemplate}
                name="template"
            />

            {errors && errors[0] &&
                <>
                    {errors?.map((er, index) =>
                        <Typography key={"error-" + index} variant="body2" color="warning.main">
                            {er.msg}
                        </Typography>
                    )}
                    <Link href={"#" + fetchRoute({ route: ERoutes.templateEdit, params: { templateId: systemEmail.template?.id } })}>{t("general-edit-template")}</Link>
                </>
            }
        </DialogContent>
        <DialogActions
            primaryBtnText={t("general-confirm")}
            primaryBtnOnClick={handleConfirm}
            primaryBtnDisabled={!isEnabled}
            secondaryBtnText={t("general-cancel")}
            secondaryBtnOnClick={handleCancel}
        />
    </Box>
}
